import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Plus, Trash, PencilSimple, FolderOpen } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LocationQR from "@/components/LocationQR";
import AuthedImage from "@/components/AuthedImage";
import EditLocationModal from "@/components/EditLocationModal";
import { locationPath } from "@/lib/locationPath";
import { toast } from "sonner";

export default function Locations() {
  const { user } = useAuth();
  const isPro = user?.tier === "pro" || user?.tier === "family";
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [l, i] = await Promise.all([api.get("/locations"), api.get("/items")]);
      setLocations(l.data); setItems(i.data);
    } catch (err) { toast.error(formatError(err)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this location? Items here will be unassigned.")) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success("Removed"); load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (loc) => { setEditing(loc); setModalOpen(true); };

  // Ancestor chain WITHOUT the leaf (since the leaf is already the card title).
  const ancestorsOf = (loc) => {
    const full = locationPath(locations, loc.id);
    if (full.length <= 1) return [];
    const byId = new Map(locations.map((l) => [l.id, l]));
    const out = [];
    let cur = loc.parent_id ? byId.get(loc.parent_id) : null;
    const seen = new Set();
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      out.unshift({ id: cur.id, name: cur.name });
      cur = cur.parent_id ? byId.get(cur.parent_id) : null;
    }
    return out;
  };

  // Build child map once for rollup totals
  const childrenOf = new Map();
  locations.forEach((l) => {
    if (!l.parent_id) return;
    if (!childrenOf.has(l.parent_id)) childrenOf.set(l.parent_id, []);
    childrenOf.get(l.parent_id).push(l.id);
  });
  const descendantIds = (rootId) => {
    const ids = new Set();
    const stack = [rootId];
    while (stack.length) {
      const cur = stack.pop();
      (childrenOf.get(cur) || []).forEach((cid) => {
        if (!ids.has(cid)) { ids.add(cid); stack.push(cid); }
      });
    }
    return ids;
  };

  return (
    <div className="space-y-8" data-testid="locations-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Locations</h1>
          <p className="mt-2 text-stoneink-700">Rooms, shelves, boxes — wherever things live.</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-location-btn"
          className="h-12 px-5 rounded-2xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-semibold flex items-center gap-2 active:scale-[0.98] shadow-ambient"
        >
          <Plus size={18} weight="bold" /> Add location
        </button>
      </div>

      {locations.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-sand-300 bg-white p-10 text-center" data-testid="locations-empty">
          <MapPin size={32} weight="duotone" className="mx-auto text-stoneink-500" />
          <p className="mt-3 text-stoneink-700">No locations yet. Add your first room above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {locations.map((l, i) => {
            const directCount = items.filter((it) => it.location_id === l.id).length;
            const descIds = descendantIds(l.id);
            const nestedCount = items.filter((it) => descIds.has(it.location_id)).length;
            const totalCount = directCount + nestedCount;
            const subCount = (childrenOf.get(l.id) || []).length;
            const ancestors = ancestorsOf(l);
            return (
              <motion.div key={l.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="rounded-3xl border border-sand-300 bg-white shadow-ambient hover:shadow-hover transition-all overflow-hidden flex flex-col"
                data-testid={`location-card-${l.id}`}
              >
                {/* Cover image banner (or warm fallback gradient) */}
                <div className="relative aspect-[16/10] bg-gradient-to-br from-sage-400/30 via-sand-200 to-terracotta-50">
                  {l.cover_image ? (
                    <AuthedImage path={l.cover_image} alt={l.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center text-sage-600 opacity-60">
                      <MapPin size={42} weight="duotone" />
                    </div>
                  )}
                  {/* Top-right action chips */}
                  <div className="absolute top-3 right-3 flex items-center gap-1">
                    <LocationQR location={l} onUpdated={(u) => setLocations((arr) => arr.map((x) => x.id === u.id ? u : x))} canUseQr={isPro} />
                    <button
                      onClick={() => openEdit(l)}
                      className="h-8 w-8 grid place-items-center rounded-full bg-white/90 backdrop-blur hover:bg-white text-stoneink-700"
                      data-testid={`edit-location-${l.id}`}
                      aria-label="Edit location"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => remove(l.id)}
                      className="h-8 w-8 grid place-items-center rounded-full bg-white/90 backdrop-blur hover:bg-white text-stoneink-700 hover:text-destructive"
                      data-testid={`delete-location-${l.id}`}
                      aria-label="Delete location"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                  {/* Bottom stats pill overlay */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stoneink-900/75 backdrop-blur-sm text-white text-xs font-medium"
                      data-testid={`location-item-count-${l.id}`}
                      title={nestedCount > 0 ? `${directCount} directly here, ${nestedCount} in sub-locations` : `${directCount} item${directCount === 1 ? "" : "s"} stored here`}
                    >
                      <span className="font-semibold">{totalCount}</span> item{totalCount === 1 ? "" : "s"}
                      {subCount > 0 && (
                        <>
                          <span className="opacity-50">·</span>
                          <FolderOpen size={12} weight="duotone" />
                          <span><span className="font-semibold">{subCount}</span> spots</span>
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* Card body */}
                <div className="p-5 flex flex-col flex-1">
                  {ancestors.length > 0 && (
                    <div className="flex items-center flex-wrap gap-1 text-xs text-stoneink-500 mb-2" data-testid={`location-breadcrumb-${l.id}`}>
                      <span className="font-bold uppercase tracking-[0.18em] text-[10px] text-stoneink-500 mr-1">Inside</span>
                      {ancestors.map((a, idx) => (
                        <span key={a.id} className="flex items-center gap-1">
                          <Link
                            to={`/items?location=${a.id}`}
                            className="px-2 py-0.5 rounded-full bg-sand-200 hover:bg-sage-400/20 hover:text-sage-600 text-stoneink-700 font-medium transition-colors"
                            data-testid={`breadcrumb-link-${a.id}`}
                          >
                            {a.name}
                          </Link>
                          {idx < ancestors.length - 1 && <span className="text-stoneink-500">›</span>}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="font-heading text-xl text-stoneink-900 font-medium">{l.name}</div>
                  {l.description && <div className="text-sm text-stoneink-700 mt-1.5">{l.description}</div>}

                  <div className="mt-auto pt-4">
                    <Link to={`/items?location=${l.id}`} className="text-sm text-terracotta-600 hover:text-terracotta-700 font-medium">Browse contents →</Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <EditLocationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        initial={editing}
        allLocations={locations}
      />
    </div>
  );
}
